<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden bg-hazel-50">
    <div v-if="isLoading" class="flex min-h-[50vh] flex-1 items-center justify-center">
      <Spinner />
    </div>
    <template v-else>
      <div class="min-h-0 flex-1 overflow-y-auto py-6">
        <div class="mx-auto w-full max-w-[210mm] bg-white px-6 py-6">
          <ScaledContent>
            <div class="w-[210mm] max-w-full" v-html="contractHtml"></div>
          </ScaledContent>

          <div class="mt-8 space-y-6 border-t border-gray-100 pt-8">
            <div class="relative z-20 space-y-4">
              <Checkbox v-model="gtcConfirmed" :label="t('confirmGtc')" />
              <Checkbox v-model="iTakePicturesConfirmed" :label="t('confirmITakePictures')" />
              <Checkbox
                v-if="isSportsCar"
                v-model="noRacetracksConfirmed"
                :label="t('confirmNoRacetracks')"
              />
              <Checkbox
                v-for="(customCheckbox, index) in customCheckboxLabels"
                :key="customCheckbox"
                :label="customCheckbox"
                @change="customCheckboxesConfirmed[index] = $event"
              />
            </div>

            <div class="ml-auto w-full max-w-[450px] overflow-visible">
              <div
                class="relative flex h-[140px] w-full items-stretch overflow-visible border-b border-black"
              >
                <div
                  v-if="!signingActive"
                  class="flex h-full w-full items-center bg-gray-100 p-2 font-medium text-gray-500"
                >
                  <span class="w-full text-center">{{ t('pleaseConfirm') }}</span>
                </div>

                <canvas
                  v-show="signingActive"
                  ref="signatureCanvasRef"
                  class="absolute ml-[-70px] mr-[-70px] mt-[-80px] h-[340px] w-[560px] touch-none"
                  width="560"
                  height="340"
                  data-testid="counter-signature-canvas"
                />
              </div>
              <p class="relative z-20 mt-1 text-right">{{ t('signature') }}</p>
            </div>

            <div class="relative z-20 flex justify-end gap-4">
              <CVButton
                variant="ghost"
                :disabled="!signingActive || isSaving"
                @click="clearSignature"
              >
                {{ t('resetSignature') }}
              </CVButton>
              <CVButton
                variant="default"
                :disabled="!canSubmit || isSaving"
                :is-loading="isSaving"
                @click="submitSignature"
              >
                {{ t('confirmSignature') }}
              </CVButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import Checkbox from '@/components/Checkbox.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import ScaledContent from '@/components/ScaledContent.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { counterProcessSessionService } from '@/api/counter-process-session.service';
import { useCounterContractTemplate } from '@/queries/use-counter-process-session';
import { VehicleCategory } from '@/entities/vehicle-category.enum';
import { useI18n } from 'vue-i18n';
import { create as createDrawingBoard } from 'simple-drawing-board';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  counterId: string;
  bookingId: string;
  vehicleCategories?: VehicleCategory[];
  customContractCheckboxLabels?: string[];
}>();

const emit = defineEmits<{
  (e: 'signed'): void;
}>();

const { t } = useI18n();
const gtcConfirmed = ref(false);
const iTakePicturesConfirmed = ref(false);
const noRacetracksConfirmed = ref(false);
const customCheckboxesConfirmed = ref<boolean[]>([]);
const signatureCanvasRef = ref<HTMLCanvasElement>();
const drawingBoard = ref<ReturnType<typeof createDrawingBoard>>();
const hasSignature = ref(false);
const isSaving = ref(false);

const { data: contractHtml, isLoading } = useCounterContractTemplate(
  computed(() => props.counterId),
);

const customCheckboxLabels = computed(() => props.customContractCheckboxLabels ?? []);

const isSportsCar = computed(() => {
  const categories = props.vehicleCategories ?? [];
  return [VehicleCategory.SPORTS_CAR, VehicleCategory.SUPER_CAR].some((category) =>
    categories.includes(category),
  );
});

watch(
  customCheckboxLabels,
  (labels) => {
    customCheckboxesConfirmed.value = labels.map(() => false);
  },
  { immediate: true },
);

const allDeclarationsConfirmed = computed(
  () =>
    gtcConfirmed.value &&
    iTakePicturesConfirmed.value &&
    (noRacetracksConfirmed.value || !isSportsCar.value) &&
    customCheckboxesConfirmed.value.filter((confirmed) => confirmed).length ===
      customCheckboxLabels.value.length,
);

const signingActive = computed(() => allDeclarationsConfirmed.value);

const canSubmit = computed(() => signingActive.value && hasSignature.value);

watch(signingActive, async (active) => {
  if (!active) {
    drawingBoard.value?.clear();
    hasSignature.value = false;
    return;
  }
  await nextTick();
  if (!signatureCanvasRef.value) return;
  drawingBoard.value = createDrawingBoard(signatureCanvasRef.value);
  drawingBoard.value.observer.on('drawEnd', () => {
    hasSignature.value = true;
  });
});

const clearSignature = () => {
  drawingBoard.value?.clear();
  hasSignature.value = false;
};

const getSignatureBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve));

const submitSignature = async () => {
  if (!signatureCanvasRef.value || !canSubmit.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const blob = await getSignatureBlob(signatureCanvasRef.value);
    if (!blob) return;
    const { fileKey } = await counterProcessSessionService.uploadSignature(props.counterId, blob);
    await counterProcessSessionService.captureSignature(props.counterId, {
      signatureFileKey: fileKey,
    });
    emit('signed');
  } finally {
    isSaving.value = false;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "confirmGtc": "I have read and accept the terms and conditions and privacy agreements.",
    "confirmITakePictures": "I take my own pictures of the vehicle and in particular the rims, the front bumper and the rear bumper to provide as evidence in the event of damage.",
    "confirmNoRacetracks": "I do not drive the rental car on race tracks (incl. \"tourist drives\").",
    "pleaseConfirm": "Please confirm the declarations first",
    "signature": "Signature of renter/CarVia",
    "resetSignature": "Reset signature",
    "confirmSignature": "Confirm signature"
  },
  "de": {
    "confirmGtc": "Ich habe die AGB und Datenschutzverbeinbarungen gelesen und akzeptiere diese.",
    "confirmITakePictures": "Ich mache eigene Bilder vom Fahrzeug und insbesondere den Felgen, der Frontsstoßstange und der Heckstoßstange, um sie im Schadensfall zum Beweis bereitzustellen.",
    "confirmNoRacetracks": "Ich fahre mit dem Mietwagen nicht auf Rennstrecken (inkl. \"Touristenfahrten\").",
    "pleaseConfirm": "Bitte bestätigen Sie zuerst die Erklärungen",
    "signature": "Unterschrift Mieter/CarVia",
    "resetSignature": "Unterschrift zurücksetzen",
    "confirmSignature": "Unterschrift bestätigen"
  },
  "es": {
    "confirmGtc": "He leído y acepto las condiciones generales y los acuerdos de privacidad.",
    "confirmITakePictures": "Tomo mis propias fotos del vehículo y en particular de las llantas, el paragolpes delantero y el trasero para aportarlas como prueba en caso de daños.",
    "confirmNoRacetracks": "No conduzco el vehículo de alquiler en circuitos (incl. «vueltas turísticas»).",
    "pleaseConfirm": "Confirme primero las declaraciones",
    "signature": "Firma del arrendatario/CarVia",
    "resetSignature": "Restablecer firma",
    "confirmSignature": "Confirmar firma"
  }
}
</i18n>
